import { useCreateSkillAction } from "@/action/use-create-skill-action";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { ChipCombobox } from "@/components/ui/chip-combobox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useExpertSkillsData } from "@/data/use-expert-skills-data";
import { useGroupKitExpertsData } from "@/data/use-group-kit-experts-data";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { useWizard } from "@/hooks/useWizard";
import type { ImportProgress } from "@/lib/bulk-import";
import {
  createWithProgress,
  handleImportResults,
  initializeProgresses,
} from "@/lib/bulk-import";
import { cn } from "@/lib/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faArrowRight,
  faDownload,
  faFileImport,
  faTrash,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Row } from "../ui/flex";
import { SkillCategorySelect } from "./SkillCategorySelect";
import {
  downloadSkillCsvTemplate,
  parseSkillsFromCsv,
  validateSkills,
  type SkillRow,
} from "./lib";

interface BulkCreateSkillButtonProps {
  /** When provided, skills are auto-linked to this expert; expert picker is hidden */
  expertUuid?: string;
  /** The expert's kit uuid — required when expertUuid is provided */
  kitUuid?: string;
  /** Controlled open state — used by ExpertCreatorMenu which owns its own trigger */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function BulkCreateSkillButton({
  expertUuid: expertUuidProp,
  kitUuid: expertKitUuidProp,
  open: openProp,
  onOpenChange,
}: BulkCreateSkillButtonProps) {
  const groupUuid = useGroupUuid();
  const { create: createSkill } = useCreateSkillAction();
  const { associateSkill } = useExpertSkillsData(groupUuid, null, null);
  const { experts } = useGroupKitExpertsData(expertUuidProp ? null : groupUuid);

  const [skillRows, setSkillRows] = useState<SkillRow[]>([]);
  const [selectedExpertUuids, setSelectedExpertUuids] = useState<string[]>(
    expertUuidProp ? [expertUuidProp] : [],
  );
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string[]>
  >({});
  const [progresses, setProgresses] = useState<Map<string, ImportProgress>>(
    new Map(),
  );
  const [isProcessing, setIsProcessing] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const wizard = useWizard({
    steps: ["import", "review"] as const,
    canNext: (stepIndex) => {
      if (stepIndex === 0) return skillRows.length > 0;
      return true;
    },
  });

  const hasErrors = Object.keys(validationErrors).length > 0;
  const isControlled = openProp !== undefined;

  const handleFileImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target?.result as string;
      setSkillRows(parseSkillsFromCsv(text));
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleExpertSelection = (expertUuid: string, selected: boolean) => {
    setSelectedExpertUuids((prev) =>
      selected ? [...prev, expertUuid] : prev.filter((u) => u !== expertUuid),
    );
  };

  const updateRow = (id: string, updates: Partial<SkillRow>) => {
    setSkillRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, ...updates } : row)),
    );
  };

  const removeRow = (id: string) => {
    if (skillRows.length > 1) {
      setSkillRows((prev) => prev.filter((r) => r.id !== id));
    }
  };

  const updateProgress = (id: string, update: Partial<ImportProgress>) => {
    setProgresses((prev) => {
      const next = new Map(prev);
      const current = next.get(id)!;
      next.set(id, { ...current, ...update });
      return next;
    });
  };

  const handleCreate = async () => {
    if (!groupUuid) return;

    const { errors } = validateSkills(skillRows);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors({});

    type ExpertAssoc = { expertKitUuid: string; expertUuid: string };
    let expertAssociations: ExpertAssoc[] = [];

    if (expertUuidProp && expertKitUuidProp) {
      expertAssociations = [
        { expertKitUuid: expertKitUuidProp, expertUuid: expertUuidProp },
      ];
    } else if (selectedExpertUuids.length > 0) {
      expertAssociations = selectedExpertUuids.flatMap((expertUuid) => {
        const expert = experts.find((e) => e.uuid === expertUuid);
        return expert
          ? [{ expertKitUuid: expert.expertKitUuid, expertUuid }]
          : [];
      });
    }

    setIsProcessing(true);
    setProgresses(
      initializeProgresses(
        skillRows.map((r) => ({ id: r.id, label: r.name || "Untitled" })),
      ),
    );

    try {
      const result = await createWithProgress(
        skillRows,
        async (row) => {
          const { kitUuid, skillUuid } = await createSkill({
            name: row.name || "Untitled Skill",
            category: row.category,
            content: row.content,
            createGroupAcl: true,
          });

          for (const { expertKitUuid, expertUuid } of expertAssociations) {
            await associateSkill({
              skillUuid,
              kitUuid: expertKitUuid,
              expertUuid,
            });
          }

          return { kitUuid, skillUuid };
        },
        updateProgress,
      );

      handleImportResults(result, skillRows.length, toast);

      if (result.failed.length === 0) {
        handleClose();
      }
    } catch {
      toast.error("Skill import failed");
    } finally {
      setIsProcessing(false);
    }
  };

  const resetState = () => {
    wizard.goTo(0);
    setSkillRows([]);
    setSelectedExpertUuids(expertUuidProp ? [expertUuidProp] : []);
    setValidationErrors({});
    setProgresses(new Map());
    setIsProcessing(false);
  };

  const handleClose = () => {
    resetState();
    onOpenChange?.(false);
  };

  const dialogContent = (
    <AlertDialogContent className="max-h-[80vh] max-w-3xl">
      <AlertDialogHeader className="flex-row items-center justify-between">
        <AlertDialogTitle>
          Import Skills ({wizard.currentStepIndex + 1} of {wizard.totalSteps})
        </AlertDialogTitle>
      </AlertDialogHeader>

      <AlertDialogDescription asChild>
        <div>
          {wizard.currentStep === "import" && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadSkillCsvTemplate}
                >
                  <FontAwesomeIcon icon={faDownload} />
                  Download Template
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <FontAwesomeIcon icon={faFileImport} />
                  Import CSV File
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={handleFileImport}
                />
              </div>
              {skillRows.length > 0 && (
                <p className="text-sm font-medium">
                  {skillRows.length} skill
                  {skillRows.length !== 1 ? "s" : ""} ready to import
                </p>
              )}

              {!expertUuidProp && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Link to Experts (optional)
                  </label>
                  <ChipCombobox
                    selected={selectedExpertUuids}
                    onSelectionChange={handleExpertSelection}
                    groups={[
                      {
                        groupName: "Experts",
                        options: experts.map((expert) => ({
                          value: expert.uuid,
                          searchableText: expert.name,
                          renderChip: () => expert.name,
                          renderOption: () => expert.name,
                        })),
                      },
                    ]}
                  />
                </div>
              )}
            </div>
          )}

          {wizard.currentStep === "review" && (
            <div className="max-h-[400px] space-y-2 overflow-y-auto">
              {skillRows.map((row) => {
                const progress = progresses.get(row.id);
                const rowErrors = validationErrors[row.id] ?? [];
                const hasRowErrors = rowErrors.length > 0;

                return (
                  <div
                    key={row.id}
                    className={cn("space-y-2 rounded-lg border p-3", {
                      "border-red-200 bg-red-50":
                        (hasRowErrors && !progress) ||
                        progress?.status === "failed",
                    })}
                  >
                    <div className="grid grid-cols-[1fr_auto_auto] items-end gap-2">
                      <div className="space-y-1">
                        <Label>Name</Label>
                        <Input
                          placeholder="Skill name"
                          value={row.name}
                          onChange={(e) => {
                            updateRow(row.id, {
                              name: e.target.value,
                              label: e.target.value,
                            });
                          }}
                          disabled={isProcessing}
                          className={cn({
                            "border-red-500": rowErrors.some((e) =>
                              e.toLowerCase().includes("name"),
                            ),
                          })}
                        />
                      </div>
                      <div className="w-44">
                        <SkillCategorySelect<true>
                          value={row.category}
                          onValueChange={(cat) =>
                            updateRow(row.id, { category: cat })
                          }
                          includeNoneOption={true}
                          noneOptionLabel="No category"
                          disabled={isProcessing}
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mb-0.5"
                        onClick={() => removeRow(row.id)}
                        disabled={skillRows.length === 1 || isProcessing}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </div>

                    {progress && (
                      <Row className="items-center gap-2 text-sm">
                        <span
                          className={cn({
                            "text-red-600": progress.status === "failed",
                            "text-green-600": progress.status === "completed",
                          })}
                        >
                          {progress.status === "pending" && "Pending..."}
                          {progress.status === "creating" && "Creating..."}
                          {progress.status === "completed" && "Created"}
                          {progress.status === "failed" && "Failed"}
                        </span>
                        {progress.error && (
                          <span className="text-xs text-red-600">
                            {progress.error}
                          </span>
                        )}
                      </Row>
                    )}

                    {!progress && hasRowErrors && (
                      <div className="text-xs text-red-600">
                        {rowErrors.map((err) => (
                          <p key={err}>{err}</p>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </AlertDialogDescription>

      <AlertDialogFooter>
        <div className="flex w-full items-center justify-between">
          <div>
            {wizard.canPrev && (
              <Button
                size="sm"
                variant="outline"
                onClick={wizard.prev}
                disabled={isProcessing}
              >
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                Back
              </Button>
            )}
          </div>

          <div className="flex gap-2">
            <AlertDialogCancel onClick={handleClose}>Cancel</AlertDialogCancel>

            {wizard.currentStep === "import" && (
              <Button
                size="sm"
                onClick={wizard.next}
                disabled={!wizard.canNext}
              >
                Next
                <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
              </Button>
            )}

            {wizard.currentStep === "review" && (
              <AlertDialogAction
                onClick={handleCreate}
                disabled={isProcessing || hasErrors || skillRows.length === 0}
              >
                {isProcessing
                  ? "Importing..."
                  : `Import ${skillRows.length} Skill${skillRows.length !== 1 ? "s" : ""}`}
              </AlertDialogAction>
            )}
          </div>
        </div>
      </AlertDialogFooter>
    </AlertDialogContent>
  );

  if (isControlled) {
    return (
      <AlertDialog
        open={openProp}
        onOpenChange={(open) => {
          if (!open) resetState();
          onOpenChange?.(open);
        }}
      >
        {dialogContent}
      </AlertDialog>
    );
  }

  return (
    <AlertDialog
      onOpenChange={(open) => {
        if (!open) resetState();
      }}
    >
      <AlertDialogTrigger asChild>
        <Button variant="outline" size="default">
          <FontAwesomeIcon icon={faFileImport} />
          Import Skills
        </Button>
      </AlertDialogTrigger>
      {dialogContent}
    </AlertDialog>
  );
}
